// The page held still behind a modal dialog: while one is open, whatever would scroll the page (the wheel, a finger, the
// keys) is cancelled unless something on top of the page can take it, a tall dialog or a text field scrolling inside
// itself. Nothing about the page changes, so the browser's bars and the scroll position are left alone. The listeners
// are on only while a modal is open: a cancellable wheel or touch listener on the document makes every scroll wait
// for the script. holdPageFrom(el) is the same for one element that floats over the page (the notch), always, since its
// listeners only make a scroll that starts on it wait.
import { TYPING } from './ui.js';

const modalOpen = () => !!document.querySelector('dialog:modal');

// Whether an element between `el` and the page can still move in this direction. The page's own scroller does not
// count, and what lies behind the modal is inert, so it is never the target.
export function canScroll(el, dx, dy) {
  for (; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(el);
    if (dy && /auto|scroll/.test(overflowY)) {
      if (dy < 0 ? el.scrollTop > 0 : el.scrollTop + el.clientHeight < el.scrollHeight - 1) return true;
    }
    if (dx && /auto|scroll/.test(overflowX)) {
      if (dx < 0 ? el.scrollLeft > 0 : el.scrollLeft + el.clientWidth < el.scrollWidth - 1) return true;
    }
  }
  return false;
}

// The backdrop is the dialog as far as events go, so a point outside its box is on no scroller at all.
function onBackdrop(e) {
  if (e.target?.tagName !== 'DIALOG') return false;
  const r = e.target.getBoundingClientRect();
  return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
}

function onWheel(e) {
  if (!modalOpen()) return release();
  if (e.ctrlKey) return; // a pinch on a trackpad zooms, it does not scroll
  if (onBackdrop(e) || !canScroll(e.target, e.deltaX, e.deltaY)) e.preventDefault();
}

// A finger's direction is only known from its moves, each measured from the one before.
const fingerAt = (e) => (e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null);
// Cancels this move unless something can take it; answers where the finger is now.
function holdTouch(e, at) {
  if (!at || e.touches.length !== 1 || !e.cancelable) return at; // two fingers zoom
  const { clientX: x, clientY: y } = e.touches[0];
  const dx = at.x - x;
  const dy = at.y - y;
  // input[type=range]: the finger drags the slider. The axis the finger mostly moves on, or a sideways wobble in a list at
  // its end would let the page move.
  if (!e.target.closest?.('input[type=range]') && !canScroll(e.target, Math.abs(dx) > Math.abs(dy) ? dx : 0, Math.abs(dy) >= Math.abs(dx) ? dy : 0)) e.preventDefault();
  return { x, y };
}
let touchAt = null;
function onTouchStart(e) { touchAt = fingerAt(e); }
function onTouchMove(e) {
  if (!modalOpen()) return release();
  touchAt = holdTouch(e, touchAt);
}

const KEYS = {
  ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
  PageUp: [0, -1], PageDown: [0, 1], Home: [0, -1], End: [0, 1], ' ': [0, 1],
};
function onKey(e) {
  if (!modalOpen()) return release();
  const key = KEYS[e.key];
  if (!key || e.ctrlKey || e.metaKey || e.altKey || e.target.closest?.(TYPING)) return;
  if (e.key === ' ' && e.target.closest?.('button, a, summary')) return; // it presses them, and scrolls nothing
  const dy = e.key === ' ' && e.shiftKey ? -1 : key[1];
  if (!canScroll(e.target, key[0], dy)) e.preventDefault();
}

const EVENTS = [['wheel', onWheel], ['touchstart', onTouchStart], ['touchmove', onTouchMove], ['keydown', onKey]];
let holding = false;
function hold() {
  if (holding) return;
  holding = true;
  for (const [name, fn] of EVENTS) document.addEventListener(name, fn, { capture: true, passive: false });
}
// Also from the handlers: a dialog taken out of the page while open sets off no change of its `open` attribute.
function release() {
  if (!holding) return;
  holding = false;
  touchAt = null;
  for (const [name, fn] of EVENTS) document.removeEventListener(name, fn, { capture: true });
}

/** Scrolling that starts on `el` never moves the page: overscroll-behavior only holds where there is something to scroll. */
export function holdPageFrom(el) {
  let at = null;
  el.addEventListener('wheel', (e) => { if (!e.ctrlKey && !canScroll(e.target, e.deltaX, e.deltaY)) e.preventDefault(); }, { passive: false });
  el.addEventListener('touchstart', (e) => { at = fingerAt(e); }, { passive: true });
  el.addEventListener('touchmove', (e) => { at = holdTouch(e, at); }, { passive: false });
}

export function init() {
  // Whoever opens a dialog (the page, a widget's Edit, an applet), showModal() sets this attribute and close() takes it off.
  new MutationObserver(() => (modalOpen() ? hold() : release()))
    .observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
}
